import React from 'react';
import { Select } from '@chakra-ui/react';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/max';

/**
 * Which country a bare national number belongs to.
 *
 * ★ WHY THIS EXISTS INSTEAD OF `react-phone-input-2`.
 * That widget is an INTERNATIONAL-format field: it owns the whole value and
 * glues the selected dial code onto whatever is typed. Measured in the browser,
 * typing `923005557777` with the UAE selected produced `+971 923 005 557 777` —
 * it turned a correct Pakistani number into a number that does not exist. Two of
 * the five formats this feature must accept carry their own country code, so a
 * widget that always prepends one cannot accept them.
 *
 * Splitting the two controls matches what the server already asks for: a `region`
 * to assume, and a `to` it reads for itself. A number written with `+` or `00`
 * states its own country and the server's ladder lets that win, so this select
 * only decides the ambiguous case — which is exactly what it claims to do.
 *
 * The list is derived from the phone metadata already bundled for validation,
 * so it can never disagree with what the server will accept, and no flag assets
 * are needed: an ISO2 code maps onto its regional-indicator emoji.
 */

/** "PK" → 🇵🇰, using the regional-indicator block. */
const flagOf = (iso2) =>
	String.fromCodePoint(...[...iso2].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

const nameOf = (() => {
	let display = null;
	try {
		display = new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' });
	} catch (e) {
		display = null;
	}
	return (iso2) => {
		try {
			return display?.of(iso2) ?? iso2;
		} catch (e) {
			return iso2;
		}
	};
})();

/** Every country the validator knows, named and sorted for a human. */
const ALL = getCountries()
	.map((iso2) => ({ iso2, dial: getCountryCallingCode(iso2), name: nameOf(iso2) }))
	.sort((a, b) => a.name.localeCompare(b.name));

export default function CountrySelect({ value, onChange, preferred, ...rest }) {
	// The tenant's own country first: most customers of a business are in the
	// market it sells in, and scrolling past 244 others to reach it is the whole
	// difference between a usable picker and a tolerated one.
	const options = React.useMemo(() => {
		if (!preferred) return ALL;
		const top = ALL.find((c) => c.iso2 === preferred);
		return top ? [top, ...ALL.filter((c) => c.iso2 !== preferred)] : ALL;
	}, [preferred]);

	return (
		<Select
			value={value ?? ''}
			onChange={(e) => onChange(e.target.value || null)}
			bg="bg.input"
			aria-label="Country"
			{...rest}
		>
			{options.map((c) => (
				<option key={c.iso2} value={c.iso2}>
					{flagOf(c.iso2)} {c.name} +{c.dial}
				</option>
			))}
		</Select>
	);
}
